import React from "react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Target, Eye, Heart, Shield, TrendingUp, Users, Award, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function APropos() {
  const { data: team = [] } = useQuery({
    queryKey: ['team'],
    queryFn: async () => { try { return await base44.entities.TeamMember.list('order'); } catch { return []; } },
    retry: false
  });

  const values = [
    { icon: Shield, title: "Transparence", description: "Chaque transaction est tracée, chaque participant reçoit un reçu numérique vérifié." },
    { icon: Award, title: "Crédibilité", description: "Structure juridique solide et partenaires fonciers agréés." },
    { icon: TrendingUp, title: "Innovation", description: "Intégration d'outils digitaux : paiements en ligne, reçus automatiques, plateforme de suivi." },
    { icon: Users, title: "Proximité", description: "Accompagnement humain et personnalisé de chaque client." },
    { icon: CheckCircle, title: "Sécurité", description: "Gestion rigoureuse des titres fonciers et contrôle administratif." }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">À Propos de <span className="text-[#c41e3a]">R2B Entreprises</span></h1>
            <p className="text-xl text-gray-200">Une société immobilière innovante qui associe expertise foncière, technologie digitale et modèles financiers participatifs</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              <strong className="text-black">R2B Entreprises</strong> est une société ivoirienne spécialisée dans la promotion foncière et la construction, offrant des solutions de vente de terrains et maisons ainsi qu'un modèle de <strong>préfinancement participatif sécurisé</strong> permettant aux investisseurs d'acquérir des parts de lotissement et d'être récompensés par des terrains proportionnels à leur apport.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Nous conjuguons <strong className="text-black">rigueur administrative</strong>, respect des normes foncières et innovation digitale pour rapprocher investisseurs et terrains fiables.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial={{ opacity:0,x:-30 }} whileInView={{ opacity:1,x:0 }} viewport={{ once:true }}>
              <div className="h-full border-none shadow-xl bg-gradient-to-br from-black to-gray-800 text-white p-8 rounded-xl">
                <Target className="w-16 h-16 mb-6 text-[#c41e3a]" />
                <h2 className="text-3xl font-bold mb-4">Notre Mission</h2>
                <p className="text-lg text-gray-100 leading-relaxed">Offrir à chaque citoyen la possibilité d'investir dans le foncier, d'accéder à un terrain légalement titré et d'obtenir des rendements concrets, tout en contribuant à l'aménagement structuré et durable du territoire.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity:0,x:30 }} whileInView={{ opacity:1,x:0 }} viewport={{ once:true }}>
              <div className="h-full border-none shadow-xl bg-gradient-to-br from-[#c41e3a] to-[#e63950] p-8 rounded-xl">
                <Eye className="w-16 h-16 mb-6 text-white" />
                <h2 className="text-3xl font-bold mb-4 text-white">Notre Vision</h2>
                <p className="text-lg text-white leading-relaxed">Devenir la référence de l'immobilier participatif en Afrique, en combinant innovation, transparence et inclusion dans la gestion et le financement des projets fonciers.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Heart className="w-12 h-12 mx-auto mb-4 text-[#c41e3a]" />
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Nos Valeurs</h2>
            <p className="text-xl text-gray-600">Les principes qui guident chacune de nos actions</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div key={value.title} initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }}>
                <div className="border-none shadow-lg hover:shadow-xl transition-shadow h-full p-6 rounded-xl bg-white">
                  <div className="w-14 h-14 gradient-bg rounded-lg flex items-center justify-center mb-4">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-black">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {team.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-[#c41e3a]" />
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Notre Équipe</h2>
              <p className="text-xl text-gray-600">Des professionnels passionnés à votre service</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <motion.div key={member.id} initial={{ opacity:0,scale:0.9 }} whileInView={{ opacity:1,scale:1 }} transition={{ delay:index*0.1 }} viewport={{ once:true }}>
                  <div className="border-none shadow-xl hover:shadow-2xl transition-all overflow-hidden group rounded-xl bg-white">
                    <div className="relative h-64 bg-gradient-to-br from-black to-gray-800 overflow-hidden">
                      {member.photo ? <img src={member.photo} alt={member.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        : <div className="w-full h-full flex items-center justify-center"><Users className="w-24 h-24 text-white/30" /></div>}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-black mb-1">{member.name}</h3>
                      <p className="text-[#c41e3a] font-medium mb-3">{member.role}</p>
                      {member.bio && <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 gradient-bg text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-3xl md:text-4xl font-bold mb-4">Nos Engagements</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {["Offrir un accès équitable à la propriété foncière pour tous","Contribuer à l'urbanisation planifiée et durable des territoires","Promouvoir l'investissement collectif comme levier de développement","Garantir l'authenticité et l'honnêteté dans toutes nos transactions"].map((engagement, index) => (
              <motion.div key={index} initial={{ opacity:0,x:index%2===0?-20:20 }} whileInView={{ opacity:1,x:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }}
                className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-[#c41e3a] flex-shrink-0 mt-1" />
                <p className="text-lg">{engagement}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
